import React, { useState, useEffect, useRef, useCallback } from 'react';
import LoadingLogo from './LoadingLogo';
import './Hero.css';

// TypeScript interfaces for hero.json structure
interface HeroSettings {
  typewriterSpeedMs: number;
  typewriterHoldMs: number;
  blurFadeDurationMs: number;
  deviceRaiseDurationMs: number;
  deviceLowerDurationMs: number;
  sequenceTransitionMs: number;
  commentaryDisplayMs: number;
}

interface HeroContent {
  heroHeading: string;
  heroDescription: string;
  questionText: string;
  parkAgainButtonText: string;
}

interface HeroQuestion {
  id: number;
  label: string;
  mobileLabel?: string;
  finalStatement: string;
  icon?: string;
}

interface SequenceStep {
  type: 'background' | 'ipad' | 'iphone';
  action: 'play' | 'raise' | 'lower' | 'video_ended';
  src?: string;
  start: 'immediate' | 'with_previous' | 'after_previous' | 'after_delay';
  delay: number;
  commentary_text?: string;
}

interface Sequence {
  preloadVideos: string[];
  steps: SequenceStep[];
}

interface HeroAssets {
  basePath: string;
  introVideo: string;
  backgroundVideos: string[];
  deviceVideos: string[];
  deviceImages: {
    ipad: string;
    iphone: string;
  };
}

interface HeroData {
  settings: HeroSettings;
  content: HeroContent;
  questions: HeroQuestion[];
  sequences: Record<string, Sequence>;
  assets: HeroAssets;
}

interface HeroProps {
  onIntroReady?: () => void;
  startDeferredPreload?: boolean;
}

type HeroPhase =
  | 'initial'
  | 'ready'
  | 'intro'
  | 'question'
  | 'loading_sequence'
  | 'playing_sequence'
  | 'lowering_devices'
  | 'typewriter'
  | 'fading_typewriter'
  | 'complete';

interface DeviceState {
  visible: boolean;
  raised: boolean;
  videoSrc: string | null;
}

const Hero: React.FC<HeroProps> = React.memo(({ onIntroReady, startDeferredPreload = false }) => {
  // State
  const [heroData, setHeroData] = useState<HeroData | null>(null);
  const [phase, setPhase] = useState<HeroPhase>('initial');
  const [, setSelectedQuestion] = useState<number | null>(null);
  const [typewriterText, setTypewriterText] = useState('');
  const [ipadState, setIpadState] = useState<DeviceState>({ visible: false, raised: false, videoSrc: null });
  const [iphoneState, setIphoneState] = useState<DeviceState>({ visible: false, raised: false, videoSrc: null });
  const [currentBackgroundSrc, setCurrentBackgroundSrc] = useState<string>('');
  const [hasSeenQuestions, setHasSeenQuestions] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [, setCommentaryText] = useState('');
  const [, setCommentaryVisible] = useState(false);
  const commentaryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs
  const backgroundVideoRef = useRef<HTMLVideoElement>(null);
  const backgroundVideoRef2 = useRef<HTMLVideoElement>(null);
  const ipadVideoRef = useRef<HTMLVideoElement>(null);
  const iphoneVideoRef = useRef<HTMLVideoElement>(null);
  const videoEndResolvers = useRef<Map<string, () => void>>(new Map());
  const preloadedVideos = useRef<Set<string>>(new Set());
  const isSequenceRunning = useRef(false);
  const backgroundVideoState = useRef<{ activeRef: React.RefObject<HTMLVideoElement>; nextRef: React.RefObject<HTMLVideoElement> }>({
    activeRef: backgroundVideoRef,
    nextRef: backgroundVideoRef2
  });

  // Load hero.json on mount
  useEffect(() => {
    const loadHeroData = async () => {
      try {
        const response = await fetch('/assets/hero/hero.json');
        const data: HeroData = await response.json();
        setHeroData(data);
        setCurrentBackgroundSrc(`${data.assets.basePath}${data.assets.introVideo}`);
      } catch (error) {
        console.error('Failed to load hero data:', error);
      }
    };
    loadHeroData();
  }, []);

  // Track mobile viewport to gate mobile-only behavior
  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  // Preload initial video and set to first frame
  useEffect(() => {
    if (!heroData || !backgroundVideoRef.current) return;

    const video = backgroundVideoRef.current;

    const handleCanPlay = () => {
      video.currentTime = 0;
      video.pause();
      video.style.opacity = '1';
      setPhase('ready');
      onIntroReady?.();
    };

    video.addEventListener('canplaythrough', handleCanPlay, { once: true });
    video.load();

    return () => {
      video.removeEventListener('canplaythrough', handleCanPlay);
    };
  }, [heroData, onIntroReady]);

  // Listen for first user interaction when ready
  useEffect(() => {
    if (phase !== 'ready') return;

    if (isMobile) {
      setPhase('intro');
      backgroundVideoRef.current?.play();
      return;
    }

    const handleInteraction = () => {
      setPhase('intro');
      backgroundVideoRef.current?.play();
    };

    const events = ['mousemove', 'scroll', 'click', 'touchstart', 'keydown'];
    events.forEach(e => window.addEventListener(e, handleInteraction, { once: true, passive: true }));

    return () => {
      events.forEach(e => window.removeEventListener(e, handleInteraction));
    };
  }, [phase, isMobile]);

  // Handle intro video ended
  const handleIntroVideoEnded = useCallback(() => {
    if (phase === 'intro') {
      // Keep video on last frame
      backgroundVideoRef.current?.pause();
      setPhase('question');
    }
  }, [phase]);

  // Video preloader utility
  const preloadVideo = useCallback((src: string, basePath: string): Promise<void> => {
    const fullSrc = `${basePath}${src}`;

    if (preloadedVideos.current.has(fullSrc)) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;
      video.src = fullSrc;

      const handleCanPlay = () => {
        preloadedVideos.current.add(fullSrc);
        video.remove();
        resolve();
      };

      const handleError = () => {
        console.error(`Failed to preload video: ${fullSrc}`);
        video.remove();
        reject(new Error(`Failed to load ${src}`));
      };

      video.addEventListener('canplaythrough', handleCanPlay, { once: true });
      video.addEventListener('error', handleError, { once: true });
      video.load();
    });
  }, []);

  const preloadVideos = useCallback(async (videos: string[], basePath: string): Promise<void> => {
    await Promise.all(videos.map(v => preloadVideo(v, basePath)));
  }, [preloadVideo]);

  // Preload background videos after page loading is complete to avoid blocking initial load
  useEffect(() => {
    if (!startDeferredPreload || !heroData) return;

    const preloadBackgrounds = async () => {
      try {
        // Preload background videos (excluding intro which is already loaded)
        const backgroundsToPreload = heroData.assets.backgroundVideos.filter(
          v => v !== heroData.assets.introVideo
        );
        await preloadVideos(backgroundsToPreload, heroData.assets.basePath);

        // Also preload device videos for smoother sequence playback
        await preloadVideos(heroData.assets.deviceVideos, heroData.assets.basePath);
      } catch (error) {
        console.error('Failed to preload background videos:', error);
      }
    };

    // Small delay to let the page render first
    const timer = setTimeout(preloadBackgrounds, 100);
    return () => clearTimeout(timer);
  }, [startDeferredPreload, heroData, preloadVideos]);

  // Wait for device video to end
  const waitForDeviceVideoEnd = useCallback((deviceType: 'ipad' | 'iphone'): Promise<void> => {
    return new Promise((resolve) => {
      videoEndResolvers.current.set(deviceType, resolve);
    });
  }, []);

  // Device video ended handlers
  const handleIpadVideoEnded = useCallback(() => {
    const resolver = videoEndResolvers.current.get('ipad');
    if (resolver) {
      resolver();
      videoEndResolvers.current.delete('ipad');
    }
  }, []);

  const handleIphoneVideoEnded = useCallback(() => {
    const resolver = videoEndResolvers.current.get('iphone');
    if (resolver) {
      resolver();
      videoEndResolvers.current.delete('iphone');
    }
  }, []);

  // Delay utility
  const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

  // Stop and reset any active media playback
  const stopAllMediaPlayback = useCallback(() => {
    if (backgroundVideoRef.current) {
      backgroundVideoRef.current.pause();
      backgroundVideoRef.current.currentTime = 0;
    }
    if (backgroundVideoRef2.current) {
      backgroundVideoRef2.current.pause();
      backgroundVideoRef2.current.currentTime = 0;
      backgroundVideoRef2.current.style.opacity = '0';
      backgroundVideoRef2.current.style.transition = '';
    }
    if (ipadVideoRef.current) {
      ipadVideoRef.current.pause();
      ipadVideoRef.current.currentTime = 0;
      ipadVideoRef.current.src = '';
    }
    if (iphoneVideoRef.current) {
      iphoneVideoRef.current.pause();
      iphoneVideoRef.current.currentTime = 0;
      iphoneVideoRef.current.src = '';
    }
  }, []);

  // Resolve any pending video end waiters (used when aborting sequences)
  const resolvePendingVideoEnds = useCallback(() => {
    videoEndResolvers.current.forEach(resolver => resolver());
    videoEndResolvers.current.clear();
  }, []);

  // Show commentary text with configurable display time and smooth fade
  const showCommentary = useCallback(async (text: string): Promise<void> => {
    if (!text || !heroData) return;

    // Clear any existing timeout
    if (commentaryTimeoutRef.current) {
      clearTimeout(commentaryTimeoutRef.current);
    }

    // Set text and fade in
    setCommentaryText(text);
    setCommentaryVisible(true);

    // After configured duration, start fade out
    const displayMs = heroData.settings.commentaryDisplayMs || 3000;
    commentaryTimeoutRef.current = setTimeout(() => {
      setCommentaryVisible(false);
      // Give time for fade-out animation
      commentaryTimeoutRef.current = setTimeout(() => {
        setCommentaryText('');
      }, 300);
    }, displayMs);
  }, [heroData]);

  // Typewriter animation
  const runTypewriter = useCallback(async (text: string, speedMs: number): Promise<void> => {
    setTypewriterText('');
    for (let i = 0; i <= text.length; i++) {
      if (!isSequenceRunning.current) break;
      setTypewriterText(text.slice(0, i));
      await delay(speedMs);
    }
  }, []);

  // Execute a single step
  const executeStep = useCallback(async (step: SequenceStep, basePath: string): Promise<void> => {
    if (!isSequenceRunning.current) return;

    // Respect delays for steps that should wait before starting
    if ((step.start === 'after_delay' || step.start === 'after_previous') && step.delay > 0) {
      await delay(step.delay);
      if (!isSequenceRunning.current) return;
    }

    switch (step.type) {
      case 'background':
        if (step.action === 'play' && step.src) {
          if (!isSequenceRunning.current) return;
          const videoSrc = `${basePath}${step.src}`;
          // Ensure video is preloaded
          await preloadVideo(step.src, basePath);
          if (!isSequenceRunning.current) return;
          
          // Get the next video element to load into
          const nextVideoRef = backgroundVideoState.current.activeRef === backgroundVideoRef ? backgroundVideoRef2 : backgroundVideoRef;
          const nextVideo = nextVideoRef.current;
          
          if (!nextVideo) break;
          
          // Load the new video into the inactive element
          nextVideo.src = videoSrc;
          nextVideo.currentTime = 0;
          nextVideo.style.opacity = '0';
          
          // Wait for the video to be ready
          await new Promise<void>((resolve) => {
            const handleLoadedData = () => {
              nextVideo.removeEventListener('loadeddata', handleLoadedData);
              resolve();
            };
            
            if (nextVideo.readyState >= 2) {
              resolve();
            } else {
              nextVideo.addEventListener('loadeddata', handleLoadedData, { once: true });
              // Safety timeout
              setTimeout(() => resolve(), 500);
            }
          });
          if (!isSequenceRunning.current) return;
          
          // Play the prepared video
          nextVideo.play().catch(() => {});
          
          // Show commentary if provided
          if (step.commentary_text && isSequenceRunning.current) {
            showCommentary(step.commentary_text);
          }
          
          // Crossfade: fade in next video on top of current
          nextVideo.style.transition = 'opacity 0.3s ease-in-out';
          nextVideo.style.opacity = '1';
          
          // Wait for crossfade to complete
          await delay(300);
          if (!isSequenceRunning.current) return;
          
          // Remove transition for next time
          nextVideo.style.transition = '';
          
          // Swap which video is active (but keep the old one playing in background)
          backgroundVideoState.current.activeRef = nextVideoRef;
        }
        break;

      case 'ipad':
        if (step.action === 'raise' && step.src) {
          if (!isSequenceRunning.current) return;
          const videoSrc = `${basePath}${step.src}`;
          // Reset to lowered state before raising so the animation always fires
          setIpadState({ visible: true, raised: false, videoSrc });
          requestAnimationFrame(() => {
            setIpadState(prev => ({ ...prev, raised: true, videoSrc }));
          });
          // Wait briefly for the lift animation, then start playback
          await delay(heroData?.settings.deviceRaiseDurationMs ? Math.min(heroData.settings.deviceRaiseDurationMs, 250) : 150);
          if (!isSequenceRunning.current) return;
          if (ipadVideoRef.current) {
            ipadVideoRef.current.currentTime = 0;
            ipadVideoRef.current.play().catch(() => {});
          }
          // Show commentary if provided
          if (step.commentary_text && isSequenceRunning.current) {
            showCommentary(step.commentary_text);
          }
        } else if (step.action === 'lower') {
          if (!isSequenceRunning.current) return;
          setIpadState(prev => ({ ...prev, raised: false }));
          await delay(heroData?.settings.deviceLowerDurationMs || 400);
          if (!isSequenceRunning.current) return;
          setIpadState({ visible: false, raised: false, videoSrc: null });
        } else if (step.action === 'video_ended') {
          if (!isSequenceRunning.current) return;
          await waitForDeviceVideoEnd('ipad');
          if (!isSequenceRunning.current) return;
        }
        break;

      case 'iphone':
        if (step.action === 'raise' && step.src) {
          if (!isSequenceRunning.current) return;
          const videoSrc = `${basePath}${step.src}`;
          setIphoneState({ visible: true, raised: false, videoSrc });
          requestAnimationFrame(() => {
            setIphoneState(prev => ({ ...prev, raised: true, videoSrc }));
          });
          await delay(heroData?.settings.deviceRaiseDurationMs ? Math.min(heroData.settings.deviceRaiseDurationMs, 250) : 150);
          if (!isSequenceRunning.current) return;
          if (iphoneVideoRef.current) {
            iphoneVideoRef.current.currentTime = 0;
            iphoneVideoRef.current.play().catch(() => {});
          }
          // Show commentary if provided
          if (step.commentary_text && isSequenceRunning.current) {
            showCommentary(step.commentary_text);
          }
        } else if (step.action === 'lower') {
          if (!isSequenceRunning.current) return;
          setIphoneState(prev => ({ ...prev, raised: false }));
          await delay(heroData?.settings.deviceLowerDurationMs || 400);
          if (!isSequenceRunning.current) return;
          setIphoneState({ visible: false, raised: false, videoSrc: null });
        } else if (step.action === 'video_ended') {
          if (!isSequenceRunning.current) return;
          await waitForDeviceVideoEnd('iphone');
          if (!isSequenceRunning.current) return;
        }
        break;
    }
  }, [heroData, waitForDeviceVideoEnd, preloadVideo, showCommentary]);

  // Execute full sequence
  const executeSequence = useCallback(async (questionId: number) => {
    if (!heroData || isSequenceRunning.current) return;

    isSequenceRunning.current = true;
    setSelectedQuestion(questionId);

    const sequence = heroData.sequences[questionId.toString()];
    const question = heroData.questions.find(q => q.id === questionId);

    if (!sequence || !question) {
      isSequenceRunning.current = false;
      return;
    }

    // Show loading and preload videos
    setPhase('loading_sequence');
    try {
      await preloadVideos(sequence.preloadVideos, heroData.assets.basePath);
    } catch (error) {
      console.error('Failed to preload sequence videos:', error);
    }
    if (!isSequenceRunning.current) return;

    // Fade out blur and start sequence
    setPhase('playing_sequence');
    await delay(heroData.settings.blurFadeDurationMs);
    if (!isSequenceRunning.current) return;

    // Track which devices are raised during sequence
    let ipadIsRaised = false;
    let iphoneIsRaised = false;

    // Execute steps
    let i = 0;
    while (i < sequence.steps.length && isSequenceRunning.current) {
      const step = sequence.steps[i];
      const nextStep = sequence.steps[i + 1];

      // Track device states
      if (step.type === 'ipad' && step.action === 'raise') ipadIsRaised = true;
      if (step.type === 'ipad' && step.action === 'lower') ipadIsRaised = false;
      if (step.type === 'iphone' && step.action === 'raise') iphoneIsRaised = true;
      if (step.type === 'iphone' && step.action === 'lower') iphoneIsRaised = false;

      // Check if next step should run with_previous
      if (nextStep && nextStep.start === 'with_previous') {
        // Track next step states too
        if (nextStep.type === 'ipad' && nextStep.action === 'raise') ipadIsRaised = true;
        if (nextStep.type === 'ipad' && nextStep.action === 'lower') ipadIsRaised = false;
        if (nextStep.type === 'iphone' && nextStep.action === 'raise') iphoneIsRaised = true;
        if (nextStep.type === 'iphone' && nextStep.action === 'lower') iphoneIsRaised = false;

        // Run current and next step in parallel
        await Promise.all([
          executeStep(step, heroData.assets.basePath),
          executeStep(nextStep, heroData.assets.basePath)
        ]);
        i += 2;
      } else {
        await executeStep(step, heroData.assets.basePath);
        i++;
      }
    }
    if (!isSequenceRunning.current) return;

    // Lower any raised devices BEFORE typewriter
    if (!isSequenceRunning.current) return;
    setPhase('lowering_devices');

    if (ipadIsRaised) {
      setIpadState(prev => ({ ...prev, raised: false }));
    }
    if (iphoneIsRaised) {
      setIphoneState(prev => ({ ...prev, raised: false }));
    }

    // Wait for lower animation
    if (ipadIsRaised || iphoneIsRaised) {
      await delay(heroData.settings.deviceLowerDurationMs + 200);
      if (!isSequenceRunning.current) return;
    }

    // Hide devices completely
    setIpadState({ visible: false, raised: false, videoSrc: null });
    setIphoneState({ visible: false, raised: false, videoSrc: null });

    // Clear commentary before typewriter phase
    if (commentaryTimeoutRef.current) {
      clearTimeout(commentaryTimeoutRef.current);
    }
    setCommentaryText('');
    setCommentaryVisible(false);

    // Show typewriter with blur overlay
    if (!isSequenceRunning.current) return;
    setPhase('typewriter');
    await delay(300); // Brief pause before typing
    if (!isSequenceRunning.current) return;
    await runTypewriter(question.finalStatement, heroData.settings.typewriterSpeedMs);
    if (!isSequenceRunning.current) return;

    // Hold for specified duration
    await delay(heroData.settings.typewriterHoldMs);
    if (!isSequenceRunning.current) return;

    // Fade out typewriter/blur
    setPhase('fading_typewriter');

    // Reset background to render_0 frame 1
    const introVideoSrc = `${heroData.assets.basePath}${heroData.assets.introVideo}`;
    setCurrentBackgroundSrc(introVideoSrc);
    await delay(50);
    if (!isSequenceRunning.current) return;
    
    // Reset both video elements
    if (backgroundVideoRef.current) {
      backgroundVideoRef.current.src = introVideoSrc;
      backgroundVideoRef.current.currentTime = 0;
      backgroundVideoRef.current.pause();
      backgroundVideoRef.current.style.opacity = '1';
    }
    
    if (backgroundVideoRef2.current) {
      backgroundVideoRef2.current.src = '';
      backgroundVideoRef2.current.currentTime = 0;
      backgroundVideoRef2.current.pause();
      backgroundVideoRef2.current.style.opacity = '0';
      backgroundVideoRef2.current.style.transition = '';
    }
    
    // Reset the active video state back to the first one
    backgroundVideoState.current.activeRef = backgroundVideoRef;

    // Wait for fade out
    await delay(heroData.settings.blurFadeDurationMs);
    if (!isSequenceRunning.current) return;

    // Show Park Again on top of video (no blur)
    setPhase('complete');
    isSequenceRunning.current = false;
  }, [heroData, preloadVideos, executeStep, runTypewriter]);

  // Handle question button click
  const handleQuestionClick = useCallback((questionId: number) => {
    setHasSeenQuestions(true);
    executeSequence(questionId);
  }, [executeSequence]);

  // Handle return to options
  const handleReturnToOptions = useCallback(() => {
    if (!heroData) return;

    // Stop sequence
    isSequenceRunning.current = false;

    // Resolve any pending waiters and halt media
    resolvePendingVideoEnds();
    stopAllMediaPlayback();

    // Clear commentary
    if (commentaryTimeoutRef.current) {
      clearTimeout(commentaryTimeoutRef.current);
    }
    setCommentaryText('');
    setCommentaryVisible(false);

    // Lower and hide devices
    setIpadState({ visible: false, raised: false, videoSrc: null });
    setIphoneState({ visible: false, raised: false, videoSrc: null });

    // Clear typewriter
    setTypewriterText('');
    setSelectedQuestion(null);

    // Reset background to render_0 frame 1
    const introVideoSrc = `${heroData.assets.basePath}${heroData.assets.introVideo}`;
    setCurrentBackgroundSrc(introVideoSrc);

    if (backgroundVideoRef.current) {
      backgroundVideoRef.current.src = introVideoSrc;
      backgroundVideoRef.current.currentTime = 0;
      backgroundVideoRef.current.pause();
      backgroundVideoRef.current.style.opacity = '1';
    }

    if (backgroundVideoRef2.current) {
      backgroundVideoRef2.current.src = '';
      backgroundVideoRef2.current.currentTime = 0;
      backgroundVideoRef2.current.pause();
      backgroundVideoRef2.current.style.opacity = '0';
      backgroundVideoRef2.current.style.transition = '';
    }

    backgroundVideoState.current.activeRef = backgroundVideoRef;

    // Go back to question phase
    setPhase('question');
  }, [heroData, resolvePendingVideoEnds, stopAllMediaPlayback]);

  // Handle scroll down to learn more
  const handleScrollToLearnMore = useCallback(() => {
    const trustBadgesSection = document.getElementById('trust-badges');
    if (trustBadgesSection) {
      trustBadgesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Reset to initial state (Park Again)
  const handleParkAgain = useCallback(() => {
    if (!heroData) return;

    isSequenceRunning.current = false;
    resolvePendingVideoEnds();
    stopAllMediaPlayback();

    // Reset all state
    setSelectedQuestion(null);
    setTypewriterText('');
    if (commentaryTimeoutRef.current) {
      clearTimeout(commentaryTimeoutRef.current);
    }
    setCommentaryText('');
    setCommentaryVisible(false);
    setIpadState({ visible: false, raised: false, videoSrc: null });
    setIphoneState({ visible: false, raised: false, videoSrc: null });
    setCurrentBackgroundSrc(`${heroData.assets.basePath}${heroData.assets.introVideo}`);

    // Reset both video elements
    if (backgroundVideoRef.current) {
      backgroundVideoRef.current.currentTime = 0;
      backgroundVideoRef.current.pause();
      backgroundVideoRef.current.style.opacity = '1';
    }
    
    if (backgroundVideoRef2.current) {
      backgroundVideoRef2.current.src = '';
      backgroundVideoRef2.current.currentTime = 0;
      backgroundVideoRef2.current.pause();
      backgroundVideoRef2.current.style.opacity = '0';
      backgroundVideoRef2.current.style.transition = '';
    }
    
    // Reset the active video state back to the first one
    backgroundVideoState.current.activeRef = backgroundVideoRef;

    // Back to ready state
    setPhase('ready');
  }, [heroData, resolvePendingVideoEnds, stopAllMediaPlayback]);

  // Don't render until data is loaded
  if (!heroData) {
    return (
      <div className="hero">
        <div className="hero__loading">
          <LoadingLogo text="Loading..." />
        </div>
      </div>
    );
  }

  const showTextOverlay = phase === 'initial' || phase === 'ready' || phase === 'intro' || phase === 'complete';
  const showBlurOverlay = phase === 'question' || phase === 'loading_sequence' || phase === 'typewriter';
  const isFadingBlur = phase === 'fading_typewriter';
  const showQuestion = phase === 'question';
  const showLoading = phase === 'loading_sequence';
  const showTypewriter = phase === 'typewriter' || phase === 'fading_typewriter';
  const showParkAgain = phase === 'complete';
  const showReturnToOptions = hasSeenQuestions && (
    phase === 'initial' ||
    phase === 'ready' ||
    phase === 'intro' ||
    phase === 'loading_sequence' ||
    phase === 'playing_sequence' ||
    phase === 'lowering_devices' ||
    phase === 'typewriter' ||
    phase === 'fading_typewriter' ||
    phase === 'complete'
  );

  return (
    <div className="hero">
      {/* Background Video Layer */}
      <div className="hero__video-layer">
        <video
          ref={backgroundVideoRef}
          className="hero__video hero__video--1"
          src={currentBackgroundSrc}
          muted
          playsInline
          preload="auto"
          onEnded={handleIntroVideoEnded}
        />
        <video
          ref={backgroundVideoRef2}
          className="hero__video hero__video--2"
          muted
          playsInline
          preload="none"
        />
      </div>

      {/* Hero Text Overlay - visible during intro */}
      <div className={`hero__text-overlay ${showTextOverlay ? '' : 'hero__text-overlay--hidden'}`}>
        <h1 className="hero__heading">{heroData.content.heroHeading}</h1>
        {heroData.content.heroDescription && (
          <p className="hero__description">{heroData.content.heroDescription}</p>
        )}
      </div>

      {/* Device Mockups */}
      <div className="hero__devices">
        {/* iPad */}
        {ipadState.visible && (
          <div className={`hero__device hero__device--ipad ${ipadState.raised ? 'hero__device--raised' : 'hero__device--lowering'}`}>
            <div className="hero__device-screen">
              <video
                ref={ipadVideoRef}
                className="hero__device-video"
                src={ipadState.videoSrc || ''}
                muted
                playsInline
                onEnded={handleIpadVideoEnded}
              />
            </div>
            <img
              src={`${heroData.assets.basePath}${heroData.assets.deviceImages.ipad}`}
              alt="iPad"
              className="hero__device-frame"
            />
          </div>
        )}

        {/* iPhone */}
        {iphoneState.visible && (
          <div className={`hero__device hero__device--iphone ${iphoneState.raised ? 'hero__device--raised' : 'hero__device--lowering'}`}>
            <div className="hero__device-screen">
              <video
                ref={iphoneVideoRef}
                className="hero__device-video"
                src={iphoneState.videoSrc || ''}
                muted
                playsInline
                onEnded={handleIphoneVideoEnded}
              />
            </div>
            <img
              src={`${heroData.assets.basePath}${heroData.assets.deviceImages.iphone}`}
              alt="iPhone"
              className="hero__device-frame"
            />
          </div>
        )}
      </div>

      {/* Commentary Text - positioned based on what's visible - DISABLED */}
      {/* Uncomment the block below to re-enable commentary text feature
      {commentaryText && (
        <div className={`hero__commentary ${commentaryVisible ? 'hero__commentary--visible' : 'hero__commentary--hidden'} ${
          currentBackgroundSrc && !ipadState.visible && !iphoneState.visible ? 'hero__commentary--background' : ''
        } ${
          ipadState.visible ? 'hero__commentary--ipad' : ''
        } ${
          iphoneState.visible ? 'hero__commentary--iphone' : ''
        }`}>
          <p className="hero__commentary-text">{commentaryText}</p>
        </div>
      )}
      */}

      {/* Blur Overlay with Question/Typewriter */}
      <div className={`hero__blur-overlay ${showBlurOverlay ? 'hero__blur-overlay--visible' : ''} ${isFadingBlur ? 'hero__blur-overlay--fading' : ''}`}>
        {/* Loading State */}
        {showLoading && (
          <div className="hero__loading-content">
            <LoadingLogo text="Loading experience..." />
          </div>
        )}

        {/* Question Section */}
        {showQuestion && (
          <div className="hero__question">
            <h2 className="hero__question-text">{heroData.content.questionText}</h2>
            <div className="hero__question-buttons">
              {heroData.questions.map((q) => (
                <button
                  key={q.id}
                  className="hero__question-btn"
                  onClick={() => handleQuestionClick(q.id)}
                >
                  {q.icon && (
                    <img
                      src={`${heroData.assets.basePath}${q.icon}`}
                      className="hero__question-icon"
                      alt=""
                      aria-hidden="true"
                    />
                  )}
                  <span className="hero__question-label">
                    {isMobile && q.mobileLabel ? q.mobileLabel : q.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Typewriter Section */}
        {showTypewriter && (
          <div className={`hero__typewriter ${isFadingBlur ? 'hero__typewriter--fading' : ''}`}>
            <p className="hero__typewriter-text">
              {typewriterText}
              <span className="hero__typewriter-cursor" />
            </p>
          </div>
        )}
      </div>

      {/* Return to Options Button - top of screen */}
      {showReturnToOptions && (
        <button
          className="hero__return-btn"
          onClick={handleReturnToOptions}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Return to options
        </button>
      )}

      {/* Park Again Button - directly on video, no blur */}
      {showParkAgain && (
        <div className="hero__park-again">
          <button
            className="hero__park-again-btn"
            onClick={handleParkAgain}
          >
            {heroData.content.parkAgainButtonText}
          </button>
          <button
            className="hero__scroll-hint"
            onClick={handleScrollToLearnMore}
          >
            <div className="hero__scroll-mouse">
              <div className="hero__scroll-wheel" />
            </div>
            <span>or scroll down to learn more</span>
          </button>
        </div>
      )}
    </div>
  );
});

export default Hero;
